import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { COVER_CROP_CONTAINER_STYLE, computeCoverCropWrapperStyle } from '@/lib/crop/computeCoverCropWrapperStyle';
import { ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MIN_SCENE_PREVIEW_WIDTH, MIN_SCENE_FRAME_WIDTH, type TimelineScenePreview } from '@/lib/timeline/getVisibleScenePreviews';

/** Decorative filmstrip with small seek targets; the rest of the image lets
 * pointer events through to the editable interval's drag surface. */
export function TimelineScenePreviews({
  previews,
  intervalStart,
  intervalEnd,
  pixelsPerSecond,
  frames,
  showSeekControls = true,
  focusCrop,
  sourceAspectRatio = 16 / 9,
  onSeek,
  formatTime,
}: {
  previews: TimelineScenePreview[];
  intervalStart: number;
  intervalEnd: number;
  pixelsPerSecond: number;
  frames: Map<number, string>;
  showSeekControls?: boolean;
  focusCrop?: CropCenterKeyframe;
  sourceAspectRatio?: number;
  onSeek: (time: number) => void;
  formatTime: (time: number) => string;
}) {
  const { t } = useTranslation();
  const duration = intervalEnd - intervalStart;
  if (duration <= 0) return null;
  const firstPreview = previews[0];
  const focusFrame = firstPreview && frames.get(Math.round(firstPreview.startTime * 10) / 10);
  const canShowFocus = firstPreview && (firstPreview.visibleEndTime - firstPreview.visibleStartTime) * pixelsPerSecond >= MIN_SCENE_FRAME_WIDTH;
  const compact = duration * pixelsPerSecond < 112;

  return (
    <div className="absolute inset-0 overflow-hidden rounded pointer-events-none" data-testid="interval-scene-previews">
      {previews.map((preview) => {
        const frame = frames.get(Math.round(preview.startTime * 10) / 10);
        const visibleWidth = (preview.visibleEndTime - preview.visibleStartTime) * pixelsPerSecond;
        const seekLabel = t('timeline.seekDetectedScene', { time: formatTime(preview.startTime) });
        return (
          <div
            key={`${preview.sceneStartTime}-${preview.startTime}`}
            className="absolute inset-y-0 border-l border-white/30 bg-primary/20"
            style={{
              left: `${((preview.startTime - intervalStart) / duration) * 100}%`,
              width: `${((preview.endTime - preview.startTime) / duration) * 100}%`,
              backgroundImage: frame ? `url(${frame})` : undefined,
              backgroundSize: '114px 64px',
              backgroundRepeat: 'repeat-x',
              backgroundPosition: 'left center',
            }}
          >
            {showSeekControls && visibleWidth >= MIN_SCENE_PREVIEW_WIDTH && (
              <button
                type="button"
                className="absolute bottom-1 h-6 w-6 opacity-0 group-hover/scene:opacity-100 group-focus-within/scene:opacity-100 focus-visible:opacity-100 rounded bg-black/65 text-white hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white pointer-events-auto cursor-pointer flex items-center justify-center"
                style={{ left: `calc(${((preview.visibleStartTime - preview.startTime) / (preview.endTime - preview.startTime)) * 100}% + 12px)` }}
                title={seekLabel}
                aria-label={seekLabel}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onSeek(preview.startTime);
                }}
              >
                <ScanLine className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
      {focusFrame && canShowFocus && (
        <div
          data-testid="scene-focus-thumbnail"
          className={compact ? "absolute inset-0 overflow-hidden rounded bg-black" : `absolute top-1 bottom-1 overflow-hidden rounded border border-white/70 bg-black shadow-sm ${focusCrop ? 'w-8' : 'w-12'}`}
          style={{
            containerType: COVER_CROP_CONTAINER_STYLE.containerType,
            ...(compact ? {} : { left: `clamp(12px, ${((firstPreview.visibleStartTime - intervalStart) / duration) * 100}%, calc(100% - 44px))` }),
          }}
        >
          <div style={computeCoverCropWrapperStyle({
            cropPosition: { x: focusCrop?.x ?? 50, y: focusCrop?.y ?? 50, zoom: focusCrop?.zoom ?? 1 },
            sourceAspect: sourceAspectRatio,
          })}>
            <img src={focusFrame} alt={t('timeline.focusPreview')} draggable={false} decoding="async" className="absolute inset-0 h-full w-full max-w-none" />
          </div>
        </div>
      )}
    </div>
  );
}
